import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Code2,
  ExternalLink,
} from 'lucide-react';
import { brandLogos, brandName } from '../config/brand';
import { partnerLoginUrl } from '../config/partnerPanel';
import { PartnerPanelLink } from './PartnerPanelLink';
import { SiteFooter } from './SiteFooter';
import { isIoBrandPath, isIoHeroLandingPath, isPlatformPath } from '../config/routes';
import {
  platformMegaMenuLinks,
  platformNavIcons,
} from '../config/platformNav';

interface LayoutProps {
  children: React.ReactNode;
}

const SOLUTIONS_PATHS = [
  '/smart-house-real-estate',
  '/smart-manufacturing',
  '/smart-hospitality',
  '/smart-commercial-lighting',
];

const DOCUMENTS_PATHS = ['/product-guide', '/developer-guide', '/api-documentation'];

const COMPANY_PATHS = ['/about-us', '/contact-us'];

const HOVER_CLOSE_DELAY = 150;

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const isIoBrand = isIoBrandPath(pathname);
  const isIoHeroLanding = isIoHeroLandingPath(pathname);
  const [isPlatformOpen, setIsPlatformOpen] = useState(false);
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  const [isHomeNavScrolled, setIsHomeNavScrolled] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);

  const closeAllDropdowns = useCallback(() => {
    setIsPlatformOpen(false);
    setIsDocumentsOpen(false);
    setIsSolutionsOpen(false);
    setIsCompanyOpen(false);
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenSubmenus(new Set());
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((open) => !open);
  };

  const toggleSubmenu = (submenuId: string) => {
    setOpenSubmenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(submenuId)) {
        newSet.delete(submenuId);
      } else {
        newSet.add(submenuId);
      }
      return newSet;
    });
  };

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleCloseAll = useCallback(() => {
    cancelScheduledClose();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeAllDropdowns();
      closeTimeoutRef.current = null;
    }, HOVER_CLOSE_DELAY);
  }, [cancelScheduledClose, closeAllDropdowns]);

  const openDropdown = (dropdown: 'platform' | 'solutions' | 'documents' | 'company') => {
    cancelScheduledClose();
    closeAllDropdowns();
    if (dropdown === 'platform') setIsPlatformOpen(true);
    if (dropdown === 'solutions') setIsSolutionsOpen(true);
    if (dropdown === 'documents') setIsDocumentsOpen(true);
    if (dropdown === 'company') setIsCompanyOpen(true);
  };

  useEffect(() => () => cancelScheduledClose(), [cancelScheduledClose]);

  useEffect(() => {
    if (!isIoHeroLanding) {
      setIsHomeNavScrolled(false);
      return;
    }
    const onScroll = () => {
      const next = window.scrollY > 12;
      setIsHomeNavScrolled((prev) => (prev !== next ? next : prev));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isIoHeroLanding]);

  useEffect(() => {
    closeMobileMenu();
    closeAllDropdowns();
  }, [pathname, closeAllDropdowns]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const onScroll = () => {
      closeMobileMenu();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMobileMenuOpen]);

  const isPlatformActive = isPlatformPath(pathname) || isPlatformOpen;
  const isSolutionsActive = SOLUTIONS_PATHS.includes(pathname) || isSolutionsOpen;
  const isDocumentsActive = DOCUMENTS_PATHS.includes(pathname) || isDocumentsOpen;
  const isCompanyActive = COMPANY_PATHS.includes(pathname) || isCompanyOpen;

  const showScrolledHeader = isIoBrand && (!isIoHeroLanding || isHomeNavScrolled);

  const headerLogoSrc = isIoBrand
    ? showScrolledHeader
      ? brandLogos.markDark
      : brandLogos.markWhite
    : brandLogos.markWhite;

  const headerClassName = [
    'header',
    isIoBrand ? 'header--premium' : '',
    showScrolledHeader ? 'header--scrolled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={isIoBrand ? 'layout layout--figma-home' : 'layout'}>
      <header
        className={headerClassName}
        onMouseLeave={scheduleCloseAll}
      >
        <div className="header-bar">
          <div className="header-content">
            <Link to="/" className="logo" aria-label={`${brandName} home`}>
              <img
                src={headerLogoSrc}
                alt={brandName}
                className="logo-wordmark"
                width={160}
                height={32}
              />
            </Link>

            <nav className="nav" aria-label="Main navigation">
              <div
                className="nav-dropdown"
                onMouseEnter={() => openDropdown('platform')}
              >
                <button
                  type="button"
                  className={`nav-dropdown-trigger${isPlatformActive ? ' is-active' : ''}`}
                  aria-expanded={isPlatformOpen}
                >
                  Platform
                </button>
                {isPlatformOpen && (
                  <div
                    className="mega-menu-panel mega-menu-panel--platform"
                    onMouseEnter={() => openDropdown('platform')}
                  >
                    <ul className="mega-menu-platform__links">
                      {platformMegaMenuLinks.map((link) => {
                        const isActive = pathname === link.to;
                        const isExternal = link.to.startsWith('http');
                        const LinkIcon = platformNavIcons[link.to] ?? Code2;
                        return (
                          <li key={link.to}>
                            <Link
                              to={link.to}
                              className={`mega-menu-section__link${isActive ? ' is-active' : ''}`}
                              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            >
                              <span className="mega-menu-section__link-icon" aria-hidden>
                                <LinkIcon size={20} strokeWidth={1.75} />
                              </span>
                              <span className="mega-menu-section__link-text">
                                <span className="mega-menu-section__link-label">
                                  {link.label}
                                  {link.badge ? (
                                    <span className="mega-menu-section__badge">{link.badge}</span>
                                  ) : null}
                                </span>
                                {link.description ? (
                                  <span className="mega-menu-section__link-desc">{link.description}</span>
                                ) : null}
                              </span>
                              {isExternal ? <ExternalLink size={14} aria-hidden /> : null}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="nav-dropdown"
                onMouseEnter={() => openDropdown('solutions')}
              >
                <button
                  type="button"
                  className={`nav-dropdown-trigger${isSolutionsActive ? ' is-active' : ''}`}
                  aria-expanded={isSolutionsOpen}
                >
                  Solutions
                </button>
                {isSolutionsOpen && (
                  <div className="nav-dropdown-menu nav-dropdown-menu--compact">
                    <Link
                      to="/smart-house-real-estate"
                      className={pathname === '/smart-house-real-estate' ? 'active' : ''}
                    >
                      Smart House
                    </Link>
                    <Link
                      to="/smart-manufacturing"
                      className={pathname === '/smart-manufacturing' ? 'active' : ''}
                    >
                      Smart Manufacturing
                    </Link>
                    <Link
                      to="/smart-hospitality"
                      className={pathname === '/smart-hospitality' ? 'active' : ''}
                    >
                      Smart Hospitality
                    </Link>
                    <Link
                      to="/smart-commercial-lighting"
                      className={pathname === '/smart-commercial-lighting' ? 'active' : ''}
                    >
                      Commercial Lighting
                    </Link>
                  </div>
                )}
              </div>

              <div
                className="nav-dropdown"
                onMouseEnter={() => openDropdown('documents')}
              >
                <button
                  type="button"
                  className={`nav-dropdown-trigger${isDocumentsActive ? ' is-active' : ''}`}
                  aria-expanded={isDocumentsOpen}
                >
                  Documents
                </button>
                {isDocumentsOpen && (
                  <div className="nav-dropdown-menu nav-dropdown-menu--compact">
                    <Link
                      to="/product-guide"
                      className={pathname === '/product-guide' ? 'active' : ''}
                    >
                      Product Guide
                    </Link>
                    <Link
                      to="/developer-guide"
                      className={pathname === '/developer-guide' ? 'active' : ''}
                    >
                      Developer Guide
                    </Link>
                    <Link
                      to="/api-documentation"
                      className={pathname === '/api-documentation' ? 'active' : ''}
                    >
                      API Docs
                    </Link>
                  </div>
                )}
              </div>

              <div
                className="nav-dropdown"
                onMouseEnter={() => openDropdown('company')}
              >
                <button
                  type="button"
                  className={`nav-dropdown-trigger${isCompanyActive ? ' is-active' : ''}`}
                  aria-expanded={isCompanyOpen}
                >
                  Company
                </button>
                {isCompanyOpen && (
                  <div className="nav-dropdown-menu nav-dropdown-menu--compact">
                    <Link to="/about-us" className={pathname === '/about-us' ? 'active' : ''}>
                      About Us
                    </Link>
                    <Link to="/contact-us" className={pathname === '/contact-us' ? 'active' : ''}>
                      Contact Us
                    </Link>
                  </div>
                )}
              </div>
            </nav>

            <div className="header-actions">
              <PartnerPanelLink to={partnerLoginUrl()} className="header-cta">
                Partner Login
              </PartnerPanelLink>
            </div>

            <button
              className={`mobile-menu-btn${isMobileMenuOpen ? ' is-open' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        <div className={`mobile-menu${isMobileMenuOpen ? ' active' : ''}`}>
          <div className="mobile-menu-section">
            <button
              type="button"
              className={`mobile-menu-header${isPlatformActive ? ' is-active' : ''}`}
              onClick={() => toggleSubmenu('platform')}
            >
              <span>Platform</span>
              {openSubmenus.has('platform') ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
            <div className={`mobile-submenu${openSubmenus.has('platform') ? ' active' : ''}`}>
              {platformMegaMenuLinks.map((link) => {
                const LinkIcon = platformNavIcons[link.to] ?? Code2;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={
                      pathname === link.to
                        ? 'mobile-menu-item mobile-menu-item--platform active'
                        : 'mobile-menu-item mobile-menu-item--platform'
                    }
                    onClick={closeMobileMenu}
                  >
                    <span className="mobile-menu-item__icon" aria-hidden>
                      <LinkIcon size={18} strokeWidth={1.75} />
                    </span>
                    <span className="mobile-menu-item__copy">
                      <span className="mobile-menu-item__title">
                        {link.label}
                        {link.badge ? (
                          <span className="mega-menu-section__badge">{link.badge}</span>
                        ) : null}
                      </span>
                      {link.description ? (
                        <span className="mobile-menu-item__desc">{link.description}</span>
                      ) : null}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="mobile-menu-section">
            <button
              type="button"
              className={`mobile-menu-header${isSolutionsActive ? ' is-active' : ''}`}
              onClick={() => toggleSubmenu('solutions')}
            >
              <span>Solutions</span>
              {openSubmenus.has('solutions') ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
            <div className={`mobile-submenu${openSubmenus.has('solutions') ? ' active' : ''}`}>
              <Link
                to="/smart-house-real-estate"
                className="mobile-menu-item"
                onClick={closeMobileMenu}
              >
                Smart House
              </Link>
              <Link
                to="/smart-manufacturing"
                className="mobile-menu-item"
                onClick={closeMobileMenu}
              >
                Smart Manufacturing
              </Link>
              <Link
                to="/smart-hospitality"
                className="mobile-menu-item"
                onClick={closeMobileMenu}
              >
                Smart Hospitality
              </Link>
              <Link
                to="/smart-commercial-lighting"
                className="mobile-menu-item"
                onClick={closeMobileMenu}
              >
                Commercial Lighting
              </Link>
            </div>
          </div>

          <div className="mobile-menu-section">
            <button
              type="button"
              className={`mobile-menu-header${isDocumentsActive ? ' is-active' : ''}`}
              onClick={() => toggleSubmenu('documents')}
            >
              <span>Documents</span>
              {openSubmenus.has('documents') ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
            <div className={`mobile-submenu${openSubmenus.has('documents') ? ' active' : ''}`}>
              <Link to="/product-guide" className="mobile-menu-item" onClick={closeMobileMenu}>
                Product Guide
              </Link>
              <Link to="/developer-guide" className="mobile-menu-item" onClick={closeMobileMenu}>
                Developer Guide
              </Link>
              <Link to="/api-documentation" className="mobile-menu-item" onClick={closeMobileMenu}>
                API Docs
              </Link>
            </div>
          </div>

          <div className="mobile-menu-section">
            <button
              type="button"
              className={`mobile-menu-header${isCompanyActive ? ' is-active' : ''}`}
              onClick={() => toggleSubmenu('company')}
            >
              <span>Company</span>
              {openSubmenus.has('company') ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
            <div className={`mobile-submenu${openSubmenus.has('company') ? ' active' : ''}`}>
              <Link to="/about-us" className="mobile-menu-item" onClick={closeMobileMenu}>
                About Us
              </Link>
              <Link to="/contact-us" className="mobile-menu-item" onClick={closeMobileMenu}>
                Contact Us
              </Link>
            </div>
          </div>

          <div className="mobile-menu-actions">
            <PartnerPanelLink
              to={partnerLoginUrl()}
              className="mobile-menu-item mobile-cta-btn"
              onClick={closeMobileMenu}
            >
              Partner Login
            </PartnerPanelLink>
          </div>
        </div>
      </header>

      <main
        className={
          isIoBrand ? 'main-content main-content--home-bleed' : 'main-content'
        }
      >
        {children}
      </main>

      <SiteFooter />
    </div>
  );
};
